import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import i18n from '../../i18n'
import { invalidateQueries } from '../helpers'
import { cancelGdprExport, checkGdprExport, deleteGdprExport, downloadGdprExport, generateGdprExport, getDataForExport } from './functions'
import { DataForExportApi, DownloadExportParams, ExportListParams, GrprExportResponse } from './types'

const exportData = {
    GetDataForExport: {
        useQuery: (
            module: string,
            params: ExportListParams,
            fetchData: boolean,
            exportAllData: boolean,
            options?: UseQueryOptions<DataForExportApi, AxiosError>
        ) =>
            useQuery({
                queryKey: [`${module}ForExport`, params, exportAllData],
                queryFn: () => getDataForExport(params, exportAllData, module),
                enabled: fetchData,
                staleTime: 60 * 1000,
                ...options,
            }),
    },
    CheckGdprExport: {
        useQuery: (options?: UseQueryOptions<GrprExportResponse, AxiosError>) =>
            useQuery({
                queryKey: ['gdprExport'],
                queryFn: () => checkGdprExport(),
                refetchInterval: (query) => {
                    const isInProgress = query.state.data?.items?.some((item) => item.status === 'IN_PROGRESS')
                    return isInProgress ? 5000 : false
                },
                ...options,
            }),
    },
    GenerateGdprExport: {
        useMutation: (options?: UseMutationOptions<void, AxiosError>) =>
            useMutation({
                mutationFn: () => generateGdprExport(),
                onSuccess: () => {
                    invalidateQueries(['gdprExport'])
                },
                onError: (error) => {
                    const errorMessage = i18n.t('app.common.generateExportError')
                    toast.error(errorMessage)

                    console.error('Failed to generate export:', error)
                },
                ...options,
            }),
    },
    CancelGdprExport: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, number>) =>
            useMutation({
                mutationFn: (id: number) => cancelGdprExport(id),
                onSuccess: () => {
                    invalidateQueries(['gdprExport'])
                },
                onError: (error) => {
                    const errorMessage = i18n.t('gdprExport.cancelExportError')
                    toast.error(errorMessage)

                    console.error('Failed to cancel export:', error)
                },
                ...options,
            }),
    },
    DeleteGdprExport: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, number>) =>
            useMutation({
                mutationFn: (id: number) => deleteGdprExport(id),
                onSuccess: () => {
                    invalidateQueries(['gdprExport'])
                },
                onError: (error) => {
                    const errorMessage = i18n.t('gdprExport.deleteExportError')
                    toast.error(errorMessage)

                    console.error('Failed to delete export:', error)
                },
                ...options,
            }),
    },
    DownloadGdprExport: {
        useMutation: (options?: UseMutationOptions<void, AxiosError, DownloadExportParams>) =>
            useMutation({
                mutationFn: ({ id, fileName }: DownloadExportParams) => downloadGdprExport(id, fileName),
                onSuccess: () => {
                    invalidateQueries(['gdprExport'])
                },
                onError: (error) => {
                    const errorMessage = i18n.t('gdprExport.downloadExportError')
                    toast.error(errorMessage)

                    console.error('Failed to download export:', error)
                },
                ...options,
            }),
    },
}

export default exportData
